/**
 * Rate-locks API — wraps the `/pricing` router on the eastview-os backend
 * (frontend_documentation/rate-locks.md). A lock is a full doc in
 * loan_processing.loan_locks; a summary is mirrored onto the deal as
 * `deal.loanLock` (the FULL snake_case lock doc) + `deal.loanLocked` +
 * `deal.activeLockId`.
 *
 * Request vs Confirmation split (enforced server-side):
 *  - REQUEST (request_dscr_lock / request_rtl_lock): the requester sends ONLY
 *    price inputs + optional lock_period_days / lock_notes / lock_channel
 *    (+ loan_sizer for DSCR). lock_counterparty and lock_expiration_date are
 *    STRIPPED server-side — do NOT render them on the request form.
 *  - CONFIRM (confirm_lock, capital markets): sets lock_counterparty +
 *    expiration (explicit lock_expiration_date OR lock_period_days), flips
 *    loanLocked=true.
 *
 * Field names are snake_case and spread straight into the lock pydantic model.
 */
import { losHttp } from "../lib/losHttp";

// ── enums / option lists ─────────────────────────────────────────────────────
export const LOCK_PRODUCTS = { DSCR: "dscr", RTL: "rtl" };

export const LOCK_STATUSES = [
  "Requested",
  "Confirmed",
  "Cancelled",
  "Expired",
  "Repriced",
  "Superseded",
];

// ── lock channel (rate-locks.md §"Lock channel", 2026-09-22) ────────────
// The lock's origination channel and the SOURCE OF TRUTH for what the desk calls
// the "lock type". It replaced the yes/no `is_correspondent_lock`, which could not
// express a direct (house) origination. The backend derives the old flag from this
// (Correspondent → true, Broker / Direct → false) and still accepts the flag alone
// from older clients, so send `lock_channel` and let the server keep the flag in
// step — never send both.
export const LOCK_CHANNELS = ["Direct", "Broker", "Correspondent"];

// Segmented / select options, in the order the desk reads them.
export const LOCK_CHANNEL_OPTIONS = LOCK_CHANNELS.map((value) => ({
  value,
  label: value,
}));

// Portal fallback when neither the lock nor the deal says — mirrors the tail of the
// backend's `lock_channel_for_deal`. Eastview is a correspondent channel; Lendmarq
// originates in-house (its copy of this file sets "Direct").
const PORTAL_DEFAULT_LOCK_CHANNEL = "Correspondent";

/**
 * The channel to show for an existing lock. Legacy rows carry only the flag, so
 * fall back the way the old form meant it (true → Correspondent, false → Broker).
 * A legacy DIRECT lock therefore reads "Broker" until someone edits it — that is
 * documented backend behavior, not a bug.
 */
export const lockChannelOf = (lock) => {
  const channel = lock?.lock_channel;
  if (LOCK_CHANNELS.includes(channel)) return channel;
  if (lock?.is_correspondent_lock === true) return "Correspondent";
  if (lock?.is_correspondent_lock === false) return "Broker";
  return null;
};

/**
 * Pre-selection for a NEW lock, mirroring the backend's `lock_channel_for_deal` so
 * the form shows what omitting the field would have saved: a correspondent on the
 * deal → Correspondent; else the loan's originationChannel (Broker → Broker,
 * Retail/Direct → Direct); else the portal default.
 */
export const defaultLockChannelForDeal = (deal) => {
  if (deal?.correspondentCompanyId) return "Correspondent";
  const channel = String(deal?.originationChannel || "")
    .trim()
    .toLowerCase();
  if (channel === "broker") return "Broker";
  if (channel === "retail" || channel === "direct") return "Direct";
  // The post-close flow feeds this a Salesforce opp, whose Origination_Channel__c
  // can say "Correspondent" outright (an LOS deal says it with correspondentCompanyId).
  if (channel === "correspondent") return "Correspondent";
  return PORTAL_DEFAULT_LOCK_CHANNEL;
};

// Queue lanes for /pricing/lock_queue?state=
export const LOCK_QUEUE_STATES = {
  PENDING_CONFIRMATION: "pending_confirmation",
  ACTIVE: "active",
  NEEDS_PRICING_REVIEW: "needs_pricing_review",
  CLOSED: "closed",
};

export const DSCR_MARGIN_TIERS = ["Gold", "Silver", "Bronze"];

export const RTL_BORROWER_CLASSIFICATIONS = [
  "A+",
  "A",
  "B",
  "C",
  "Not Eligible",
];

// Whitelisted by the backend's `update_lock_details` (_MUTABLE_LOCK_FIELDS).
export const MUTABLE_LOCK_FIELDS = [
  "lock_counterparty",
  "lock_notes",
  "lock_expiration_date",
  "lock_channel",
  "is_correspondent_lock",
  "preCloseReviewComplete",
  "lock_period_days",
  "assignedTo",
  "rate_at_lock",
  "base_price_for_rate",
  "loan_level_price_adjustments",
  "calculated_dscr_at_lock",
  "margin_tier",
  "margin_points_at_lock",
  "pricing_version_date",
  "pricing_grids_used_for_lock",
  "coupon_rate",
  "borrower_classification_calculated",
  "borrower_classification_override",
  "pricing_base_rate",
  "pricing_spread",
  "risk_spread_adjustment",
  "override_buy_rate",
];

// Build the multipart body for a lock request: a `submission` JSON blob (price
// inputs + optional sizerDocumentId / uploadMetadata) plus any uploaded sizer
// file(s) under `uploadFiles`. Axios sets the multipart boundary from FormData.
const lockRequestFormData = (payload, files) => {
  const fd = new FormData();
  fd.append("submission", JSON.stringify(payload || {}));
  (Array.isArray(files) ? files : files ? [files] : []).forEach((f) =>
    fd.append("uploadFiles", f),
  );
  return fd;
};

export const losLocksApi = {
  // ── request (prelim, status=Requested) ─────────────────────────────────────
  // The request endpoints are MULTIPART (same shape as
  // /files/upload_loan_docs_with_associations): `submission` is a JSON string of
  // the price inputs + optional `sizerDocumentId` (associate an existing
  // loanDocument) and/or `uploadMetadata` (per uploaded sizer file); `uploadFiles`
  // are optional uploaded sizer file(s). The backend resolves the sizer and
  // attaches it as `loan_sizer` (sets sizerParsePending for DSCR).
  //
  // POST /pricing/request_dscr_lock/{dealId}
  // payload: { rate_at_lock, base_price_for_rate, margin_tier,
  //   loan_level_price_adjustments[], calculated_dscr_at_lock, pricing_version_date,
  //   sizerDocumentId?, lock_period_days?, lock_notes?, lock_channel? }
  requestDscrLock: (dealId, payload, files) =>
    losHttp
      .post(`/pricing/request_dscr_lock/${dealId}`, lockRequestFormData(payload, files))
      .then((r) => r.data),

  // POST /pricing/request_rtl_lock/{dealId}
  // payload: { coupon_rate, pricing_base_rate, pricing_spread,
  //   risk_spread_adjustment, borrower_classification_calculated,
  //   borrower_classification_override?, classification_override_approval?,
  //   override_buy_rate?, sizerDocumentId?, lock_period_days?, lock_notes?,
  //   lock_channel? }
  requestRtlLock: (dealId, payload, files) =>
    losHttp
      .post(`/pricing/request_rtl_lock/${dealId}`, lockRequestFormData(payload, files))
      .then((r) => r.data),

  // ── confirm / update / cancel ──────────────────────────────────────────────
  // POST /pricing/confirm_lock/{dealId} (capital markets)
  // payload: { lock_counterparty, lock_expiration_date | lock_period_days,
  //   lock_confirm_date?, lock_channel?, lock_notes? }
  confirmLock: (dealId, payload) =>
    losHttp.post(`/pricing/confirm_lock/${dealId}`, payload).then((r) => r.data),

  // POST /pricing/update_lock_details/{dealId} — whitelist-only (MUTABLE_LOCK_FIELDS)
  updateLockDetails: (dealId, fields) =>
    losHttp
      .post(`/pricing/update_lock_details/${dealId}`, fields)
      .then((r) => r.data),

  // POST /pricing/cancel_lock/{dealId} { reason }
  cancelLock: (dealId, reason) =>
    losHttp
      .post(`/pricing/cancel_lock/${dealId}`, { reason })
      .then((r) => r.data),

  // POST /pricing/return_lock_for_action/{dealId} { reason, requiresException }
  // Lock desk denies / returns a requested lock to the requester — records
  // `actionNeeded` on the lock and emails the requester to update or file an
  // exception. (lock_emails.send_lock_action_needed_email)
  returnLockForAction: (dealId, { reason, requiresException } = {}) =>
    losHttp
      .post(`/pricing/return_lock_for_action/${dealId}`, {
        reason,
        requiresException: !!requiresException,
      })
      .then((r) => r.data),

  // ── extension / exception / reprice ────────────────────────────────────────
  // ── lock extension (two-step: request → confirm) ───────────────────────────
  // STEP 1 — POST /pricing/request_lock_extension/{dealId} (requester)
  // Records a PENDING extension (does NOT move the expiration) and logs a "Lock
  // Extension" pricing exception. Server computes the fee from the correspondent's
  // schedule (bps) and enforces max_lock_days (400 if the cumulative lock would
  // exceed it). Returns { lock, pendingExtension, pricingException }.
  // payload: { new_expiration_date | extension_days, reason?, fee?, assignedTo? }
  requestLockExtension: (dealId, payload) =>
    losHttp
      .post(`/pricing/request_lock_extension/${dealId}`, payload)
      .then((r) => r.data),

  // STEP 2 — POST /pricing/confirm_lock_extension/{dealId} (capital markets)
  // Applies the pending extension (moves expiration, logs to extensionHistory,
  // clears pendingExtension) or denies it. `fee` overrides the computed bps fee;
  // `deny: true` cancels the pending request (with optional `reason`).
  // Returns { lock, extension, denied? }.
  // payload: { fee?, deny?, reason? }
  confirmLockExtension: (dealId, payload) =>
    losHttp
      .post(`/pricing/confirm_lock_extension/${dealId}`, payload || {})
      .then((r) => r.data),

  // POST /pricing/request_pricing_exception/{dealId}
  // payload: a PricingException { pricingCategory, exceptionDescription, assignedTo, ... }
  requestPricingException: (dealId, payload) =>
    losHttp
      .post(`/pricing/request_pricing_exception/${dealId}`, payload)
      .then((r) => r.data),

  // POST /pricing/reprice_locked_loan/{dealId} { ...new price fields }
  repriceLockedLoan: (dealId, payload) =>
    losHttp
      .post(`/pricing/reprice_locked_loan/${dealId}`, payload)
      .then((r) => r.data),

  // POST /pricing/notify_of_pricing_variance/{dealId}
  notifyOfPricingVariance: (dealId) =>
    losHttp
      .post(`/pricing/notify_of_pricing_variance/${dealId}`, {})
      .then((r) => r.data),

  // ── reads ───────────────────────────────────────────────────────────────────
  // GET /pricing/all_locks — every lock (client-side filtering)
  getAllLocks: () => losHttp.get("/pricing/all_locks").then((r) => r.data),

  // GET /pricing/lock_queue?state=&status=&assigned_to=
  getLockQueue: (params) =>
    losHttp.get("/pricing/lock_queue", { params }).then((r) => r.data),

  // GET /pricing/locks/{dealId} — current + historical locks for a loan
  getLocksForDeal: (dealId) =>
    losHttp.get(`/pricing/locks/${dealId}`).then((r) => r.data),
};

export default losLocksApi;
